import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Lock, Mail, Loader, Eye, EyeOff } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import { useAuth } from '@/context/AuthContext';

interface LocationState {
  from?: { pathname: string };
}

const CustomerLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };
  const { login } = useAuth();

  const redirectTo = location.state?.from?.pathname || '/customer/policies';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Both fields are mandatory.');
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMsg = 'Login failed. Check credentials.';
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  return (
    <AuthLayout title="Sign In" subtitle="If you’ve purchased a protection plan, your login details were sent to your registered email address at the time of purchase.">
      <form className="tw-space-y-3 tw-relative tw-z-20 tw-mt-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>

        {/* Email Field */}
        <div className="tw-relative">
          <label
            htmlFor="email"
            className="tw-block tw-text-sm tw-font-medium tw-text-gray-400 tw-mb-1"
          >
            Email address
          </label>
          <div className="tw-relative">
            <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
              <Mail className="tw-h-5 tw-w-5 tw-text-gray-500" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="tw-py-4 tw-pl-10 tw-block focus:tw-border-b-4 tw-w-full tw-bg-transparent tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-b-[#009F40] border-b tw-text-gray-200 tw-text-sm"
              placeholder="Enter your email"
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="tw-relative">
          <label htmlFor="password" className="tw-text-sm tw-font-medium tw-text-gray-400">
            Password
          </label>
          <div className="tw-relative">
            <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
              <Lock className="tw-h-5 tw-w-5 tw-text-gray-500" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="tw-py-4 tw-pl-10 tw-block focus:tw-border-b-4 tw-w-full tw-bg-transparent tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-b-[#009F40] border-b tw-text-gray-200 tw-text-sm"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="tw-absolute tw-inset-y-0 tw-right-0 tw-pr-3 tw-flex tw-items-center tw-text-gray-500 hover:tw-text-cyan-500 tw-transition-colors tw-duration-700 focus:tw-outline-none"
            >
              {showPassword ? <EyeOff className="tw-h-5 tw-w-5" /> : <Eye className="tw-h-5 tw-w-5" />}
            </button>
          </div>
        </div>
        <div className="tw-flex tw-justify-end">
          <Link
            to="/reset-password"
            className="tw-text-xs tw-mb-5 tw-font-medium tw-text-[#00C5FB] hover:tw-text-white tw-tracking-widest tw-transition-colors tw-duration-700"
          >
            Forgot Password?
          </Link>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="tw-w-full tw-flex tw-justify-center  tw-p-4 tw-border tw-border-transparent tw-rounded-md tw-shadow-md tw-text-sm tw-font-medium tw-text-black tw-bg-[#00C5FB] hover:tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 tw-transition-colors tw-duration-700 disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
          >
            {isLoading ? <Loader className="tw-h-5 tw-w-5 tw-animate-spin" /> : 'Login'}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default CustomerLoginPage;
